(function() { window.GUID_22dc1d49_78e7_4640_8658_a0c41ca6a819 = { getGuid: function() { return "22dc1d49-78e7-4640-8658-a0c41ca6a819"; } }; })();
/**
 * Client side results engine of the score sheet finder.
 *
 * The store owns the finder state (the results returned by the server, the toolbar
 * filters and the column filters) and computes the displayed results from it. It never
 * touches the DOM nor any UI widget, which makes the filtering rules testable on their
 * own and lets the presentation layer be replaced independently.
 *
 * Only ids, eventId and rotation are sent to the server; every other filter is applied
 * here, in memory, on the results returned by the last server call.
 */
class ScoreSheetFinderStore {
    constructor() {
        /** Results of the last server call, before any client side filtering. */
        this.allResults = [];
        /** Column the results are ordered by. The server order is kept by default. */
        this.sort = undefined;
        /** Page currently shown by the list, starting at 1. */
        this.page = 1;
        /** Results selected in the list. The selection is only a visual state. */
        this.selectedIds = [];
        /** Filters driven by the toolbar, the chips and the drawers. */
        this.filters = ScoreSheetFinderState.initialFilters;
        /** Filters driven by the filter row of the results columns. */
        this.columnFilters = ScoreSheetFinderState.initialColumnFilters;
    }
    static get instance() {
        if (!this._instance) {
            this._instance = new ScoreSheetFinderStore();
        }
        return this._instance;
    }
    /** Clears the state entirely, including the results returned by the server. */
    reset() {
        this.allResults = [];
        this.sort = undefined;
        this.selectedIds = [];
        this.resetFilters();
    }
    /**
     * Clears every filter but keeps the results returned by the server. Filtering
     * always goes back to the first page; the order the user chose is not a filter and
     * is left alone.
     */
    resetFilters() {
        this.filters = ScoreSheetFinderState.initialFilters;
        this.columnFilters = ScoreSheetFinderState.initialColumnFilters;
        this.page = 1;
    }
    setResults(results) {
        this.allResults = Array.isArray(results) ? results : [];
    }
    /** Results of the last server call, before any client side filtering. */
    get results() {
        return this.allResults;
    }
    /** Results of the last server call once every client side filter is applied. */
    get filteredResults() {
        return ScoreSheetFinderStore.applyFilters(this.allResults, this.state);
    }
    /**
     * Results of the last server call, filtered and ordered: the collection both the
     * list and the cards are drawn from.
     */
    get sortedResults() {
        return ScoreSheetFinderStore.applySort(this.filteredResults, this.sort);
    }
    /** Page of results to show, given the whole filtered and ordered collection. */
    getPageResults(results) {
        if (!Array.isArray(results)) {
            return [];
        }
        const pageInfo = this.getPageInfo(results.length);
        if (!pageInfo.total) {
            return [];
        }
        return results.slice(pageInfo.from - 1, pageInfo.to);
    }
    /**
     * Describes the page to show. The page is clamped to what the results allow, so
     * narrowing the filters while on the last page never lands on an empty page.
     */
    getPageInfo(total) {
        const pageSize = ScoreSheetFinderStore.pageSize;
        const count = total > 0 ? total : 0;
        const pageCount = Math.ceil(count / pageSize);
        let page = this.page;
        if (!(page > 0)) {
            page = 1;
        }
        if (page > pageCount) {
            page = pageCount;
        }
        if (!pageCount) {
            return {
                page: 0,
                pageCount: 0,
                pageSize: pageSize,
                total: 0,
                from: 0,
                to: 0,
            };
        }
        const from = (page - 1) * pageSize + 1;
        const to = Math.min(page * pageSize, count);
        return {
            page: page,
            pageCount: pageCount,
            pageSize: pageSize,
            total: count,
            from: from,
            to: to,
        };
    }
    /**
     * Orders the results. Ordering happens on the whole filtered collection, never on
     * the page alone, so paging through the results keeps a single order.
     * Pure function: it depends on nothing but its arguments.
     */
    static applySort(results, sort) {
        if (!Array.isArray(results)) {
            return [];
        }
        if (!(sort === null || sort === void 0 ? void 0 : sort.field)) {
            return results.slice(0);
        }
        const field = sort.field;
        const direction = sort.direction === "desc" ? -1 : 1;
        return results.slice(0).sort(function (first, second) {
            return (ScoreSheetFinderStore.compareValues(first != null ? first[field] : undefined, second != null ? second[field] : undefined) * direction);
        });
    }
    /** Compares two values of a column: empty first, numbers as numbers. */
    static compareValues(first, second) {
        const firstIsEmpty = first == null || first === "";
        const secondIsEmpty = second == null || second === "";
        if (firstIsEmpty && secondIsEmpty) {
            return 0;
        }
        if (firstIsEmpty) {
            return -1;
        }
        if (secondIsEmpty) {
            return 1;
        }
        if (typeof first === "number" && typeof second === "number") {
            return first < second ? -1 : first > second ? 1 : 0;
        }
        const firstText = first.toString().toLowerCase();
        const secondText = second.toString().toLowerCase();
        return firstText < secondText ? -1 : firstText > secondText ? 1 : 0;
    }
    get state() {
        return {
            filters: this.filters,
            columnFilters: this.columnFilters,
        };
    }
    /** Payload of the next call to the Filter endpoint. */
    get filterRequest() {
        return ScoreSheetFinderStore.buildFilterRequest(this.state);
    }
    /**
     * Applies every client side filter of the state to the given results.
     * Pure function: it depends on nothing but its arguments.
     */
    static applyFilters(results, state) {
        if (!Array.isArray(results)) {
            return [];
        }
        const descriptors = ScoreSheetFinderStore.buildFilterDescriptors(state);
        if (!descriptors.length) {
            return results.slice(0);
        }
        return results.filter(function (item) {
            for (let i = 0; i < descriptors.length; i++) {
                if (!ScoreSheetFinderStore.matches(item, descriptors[i])) {
                    return false;
                }
            }
            return true;
        });
    }
    /**
     * Builds the list of filters to evaluate against a result.
     * Pure function: it depends on nothing but its arguments.
     */
    static buildFilterDescriptors(state) {
        var _a, _b, _c;
        const descriptors = [];
        const filters = state === null || state === void 0 ? void 0 : state.filters;
        if (!filters) {
            return descriptors;
        }
        const ids = (_a = filters.ids) === null || _a === void 0 ? void 0 : _a.value;
        if (Array.isArray(ids) && ids.length) {
            // Searching by id is exclusive: every other filter is dropped so that a
            // score sheet looked up by id is never hidden by another filter.
            descriptors.push({
                field: "Id",
                operator: "in",
                value: ids,
            });
            return descriptors;
        }
        ScoreSheetFinderStore.filterFields.forEach(function (field) {
            var _a;
            const item = filters[field];
            if (!item) {
                return;
            }
            const value = item.value;
            const disableIfFalse = ScoreSheetFinderStore.fieldsToDisableIfFalse.indexOf(field) > -1;
            if (disableIfFalse && (value == false || (value === null || value === void 0 ? void 0 : value.toString()) == "false")) {
                return;
            }
            if (value == null || (value === null || value === void 0 ? void 0 : value.toString()) == "") {
                return;
            }
            const isNumber = typeof value !== "boolean" &&
                value != false &&
                value != true &&
                value != "false" &&
                value != "true" &&
                !isNaN(value);
            descriptors.push({
                field: field,
                operator: (_a = ScoreSheetFinderStore.fieldOperators[field]) !== null && _a !== void 0 ? _a : "eq",
                value: isNumber ? Number(value) : value,
            });
        });
        // A score sheet based on a race to has a dynamic number of game rows, so the
        // number of games becomes a maximum instead of an exact count.
        const nbGames = descriptors.filter(function (descriptor) {
            return descriptor.field === "NbGames";
        })[0];
        if (nbGames) {
            nbGames.operator = ((_b = filters.GameRowsAreDynamic) === null || _b === void 0 ? void 0 : _b.value) == true ? "gte" : "eq";
        }
        const title = (_c = state.columnFilters) === null || _c === void 0 ? void 0 : _c.Title;
        if (title && ScoreSheetFinderStore.isApplicableColumnFilter(title)) {
            descriptors.push({
                field: "Title",
                operator: title.operator || "eq",
                value: title.value,
            });
        }
        return descriptors;
    }
    /**
     * Builds the payload of the Filter endpoint. Only ids, eventId and rotation ever
     * reach the server, and ids take precedence over everything else.
     * Pure function: it depends on nothing but its arguments.
     */
    static buildFilterRequest(state) {
        var _a, _b, _c;
        const filters = state === null || state === void 0 ? void 0 : state.filters;
        if (!filters) {
            return {};
        }
        const ids = ScoreSheetFinderStore.stringifyIds((_a = filters.ids) === null || _a === void 0 ? void 0 : _a.value);
        if (ids) {
            return {
                data: JSON.stringify({
                    ids: ids,
                }),
            };
        }
        const rotation = ScoreSheetFinderStore.stringifyRotations((_b = filters.rotations) === null || _b === void 0 ? void 0 : _b.value);
        const eventId = (_c = filters.eventId) === null || _c === void 0 ? void 0 : _c.value;
        const hasRotations = rotation.trim() != "";
        if (hasRotations && eventId) {
            return {
                data: JSON.stringify({
                    eventId: eventId,
                    rotation: rotation,
                }),
            };
        }
        if (hasRotations) {
            return {
                data: JSON.stringify({
                    rotation: rotation,
                }),
            };
        }
        if (eventId) {
            return {
                data: JSON.stringify({
                    eventId: eventId,
                }),
            };
        }
        return {};
    }
    /** Serializes the rotations the way the Filter endpoint expects them. */
    static stringifyRotations(rotations) {
        var _a;
        let stringified = "";
        try {
            if (rotations == null || !Array.isArray(rotations)) {
                return "";
            }
            for (let i = 0; i < rotations.length; i++) {
                const round = rotations[i];
                if (i != 0 && i != rotations.length - 1) {
                    stringified += "\n";
                }
                if (!round.checked || round.hidden) {
                    stringified += " -";
                    continue;
                }
                if (!((_a = round.games) === null || _a === void 0 ? void 0 : _a.length)) {
                    stringified += " -";
                    continue;
                }
                if (round.games[0].hidden ||
                    round.games[0].home == null ||
                    round.games[0].away == null ||
                    round.games[0].home == "" ||
                    round.games[0].away == "") {
                    stringified += " -";
                    continue;
                }
                for (let j = 0; j < round.games.length; j++) {
                    const game = round.games[j];
                    if (!game.home || !game.away || game.hidden) {
                        break;
                    }
                    stringified += ` ${game.home}-${game.away}`;
                }
            }
            if (!stringified || stringified.replace(/-/g, "").trim().length == 0) {
                return "";
            }
            return stringified;
        }
        catch (error) {
            console.error("Error while getting stringified rotations", error);
            return stringified;
        }
    }
    /** Serializes the selected ids the way the Filter endpoint expects them. */
    static stringifyIds(ids) {
        let stringified = "";
        if (ids == null || !Array.isArray(ids)) {
            return "";
        }
        for (let i = 0; i < ids.length; i++) {
            stringified += ids[i].toString();
            if (i < ids.length - 1) {
                stringified += ",";
            }
        }
        return stringified;
    }
    static isApplicableColumnFilter(filter) {
        if (ScoreSheetFinderStore.valuelessOperators.indexOf(filter.operator) > -1) {
            return true;
        }
        return filter.value != null && filter.value !== "";
    }
    /**
     * Evaluates a single filter against a result. String comparisons are case
     * insensitive and numbers are compared as strings when the filter value is a
     * string, so that the results stay identical to what the previous engine returned.
     */
    static matches(item, descriptor) {
        let itemValue = item != null ? item[descriptor.field] : undefined;
        let value = descriptor.value;
        if (descriptor.operator === "in") {
            return Array.isArray(value) && value.indexOf(itemValue) > -1;
        }
        if (typeof value === "string") {
            value = value.toLowerCase();
            if (typeof itemValue === "string") {
                itemValue = itemValue.toLowerCase();
            }
            else if (ScoreSheetFinderStore.isNumeric(itemValue)) {
                itemValue = itemValue + "";
            }
        }
        switch (descriptor.operator) {
            case "neq":
                return itemValue != value;
            case "gt":
                return itemValue > value;
            case "gte":
                return itemValue >= value;
            case "lt":
                return itemValue < value;
            case "lte":
                return itemValue <= value;
            case "contains":
                return (itemValue + "").indexOf(value) >= 0;
            case "doesnotcontain":
                return (itemValue + "").indexOf(value) === -1;
            case "startswith":
                return (itemValue + "").lastIndexOf(value, 0) === 0;
            case "doesnotstartwith":
                return (itemValue + "").lastIndexOf(value, 0) === -1;
            case "endswith":
                return ((itemValue + "").indexOf(value, (itemValue || "").length - (value || "").length) >= 0);
            case "doesnotendwith":
                return ((itemValue + "").indexOf(value, (itemValue || "").length - (value || "").length) < 0);
            case "isnull":
                return itemValue == null;
            case "isnotnull":
                return itemValue != null;
            case "isempty":
                return itemValue === "";
            case "isnotempty":
                return itemValue !== "";
            case "isnullorempty":
                return itemValue == null || itemValue === "";
            case "isnotnullorempty":
                return itemValue != null && itemValue !== "";
            default:
                return itemValue === value;
        }
    }
    static isNumeric(value) {
        return !isNaN(parseFloat(value)) && isFinite(value);
    }
}
/** Fields of the state that the results engine evaluates against a result. */
ScoreSheetFinderStore.filterFields = [
    "NbGames",
    "NbPlayers",
    "NbRounds",
    "IsUserTemplate",
    "IsForSingles",
    "HasDoubles",
    "HasBreakLabels",
    "HasWinBoxes",
    "HasHandicap",
    "HasFlawlessBox",
    "HasFlawlessList",
    "HasMoney",
    "HasRoundPointModifier",
    "HasTotalScoreModifier",
    "Has8Ball",
    "Has9Ball",
    "Has10Ball",
    "HasInnings",
    "HasSpecial",
    "HasPlayerRepeater",
    "IsVertical",
    "HasTotalsForVertical",
    "GameRowsAreDynamic",
];
/**
 * Operator used by each field. Every field is explicit here, including the booleans
 * that used to fall back on an implicit equality operator. NbGames is the only
 * exception: it switches to "gte" when the results are based on a race to.
 */
ScoreSheetFinderStore.fieldOperators = {
    NbGames: "eq",
    NbPlayers: "eq",
    NbRounds: "eq",
    IsUserTemplate: "eq",
    IsForSingles: "eq",
    HasDoubles: "eq",
    HasBreakLabels: "eq",
    HasWinBoxes: "eq",
    HasHandicap: "eq",
    HasFlawlessBox: "eq",
    HasFlawlessList: "eq",
    HasMoney: "eq",
    HasRoundPointModifier: "eq",
    HasTotalScoreModifier: "eq",
    Has8Ball: "eq",
    Has9Ball: "eq",
    Has10Ball: "eq",
    HasInnings: "eq",
    HasSpecial: "eq",
    HasPlayerRepeater: "eq",
    IsVertical: "eq",
    HasTotalsForVertical: "eq",
    GameRowsAreDynamic: "eq",
};
/**
 * Fields whose filter is dropped when the value is false, instead of being applied
 * as an equality against false. Turning one of those filters off means "do not
 * filter on it", not "only keep the results that do not have it".
 */
ScoreSheetFinderStore.fieldsToDisableIfFalse = [
    "IsUserTemplate",
    "HasBreakLabels",
    "HasWinBoxes",
    "HasHandicap",
    "HasFlawlessBox",
    "HasFlawlessList",
    "HasMoney",
    "HasRoundPointModifier",
    "HasTotalScoreModifier",
    "Has8Ball",
    "Has9Ball",
    "Has10Ball",
    "HasInnings",
    "HasSpecial",
    "HasPlayerRepeater",
    "IsVertical",
    "HasTotalsForVertical",
    "GameRowsAreDynamic",
];
/** Operators that filter on the presence of a value rather than on a value. */
ScoreSheetFinderStore.valuelessOperators = [
    "isnull",
    "isnotnull",
    "isempty",
    "isnotempty",
    "isnullorempty",
    "isnotnullorempty",
];
/** Number of results shown on a page of the list. */
ScoreSheetFinderStore.pageSize = 25;

//# sourceMappingURL=ScoreSheetFinderStore.js.map
